import { createApi } from '@reduxjs/toolkit/query/react';

import { IVideoRoomTokenRequest, IVideoRoomTokenResponse } from 'vcs-common';

import { IConsultation, IConsultationsParams, IJournalResult } from '../store/types';

import { baseQueryConsultationServiceWithReauth, GUEST_REQUEST_HEADER } from './api';

export const consultationsApi = createApi({
  reducerPath: 'consultationsApi',
  baseQuery: baseQueryConsultationServiceWithReauth,
  tagTypes: ['Consultations'],
  endpoints: (build) => ({
    getConsultation: build.query<IConsultation, string>({
      query: (id) => ({
        url: `/consultations/${id}`,
      }),
      providesTags: ['Consultations'],
    }),
    editConsultation: build.mutation<void, { id: number; body: { result: IJournalResult[] } }>({
      query: ({ id, body }) => ({
        url: `/consultations/${id}`,
        method: 'PUT',
        body: JSON.stringify(body),
      }),
      invalidatesTags: ['Consultations'],
    }),
    updateConsultation: build.mutation<
      IConsultation,
      {
        id: number;
        body: {
          date: string;
          datetime_start: string;
          datetime_end: string;
          client_timezone: string;
          consultation_timezone: string;
        };
      }
    >({
      query: ({ id, body }) => ({
        url: `/consultations/${id}`,
        method: 'PUT',
        body: JSON.stringify(body),
      }),
      invalidatesTags: ['Consultations'],
    }),
    deleteConsultation: build.mutation<void, { id: number; scope?: 'series' | 'this_and_future' }>({
      query: ({ id, scope }) => ({
        url: `/consultations/${id}`,
        method: 'DELETE',
        params: scope ? { scope } : undefined,
      }),
      invalidatesTags: ['Consultations'],
    }),
    getConsultationByCode: build.query<IConsultation, string>({
      query: (code) => ({
        url: `/consultations/get/${code}`,
      }),
    }),
    /**
     * `as_guest` — вход по коду комнаты без Authorization (см. GUEST_REQUEST_HEADER).
     * Нужен, когда участника групповой терапии пригласили ссылкой, а он при этом
     * залогинен под аккаунтом, не связанным с этой консультацией (например, как психолог
     * другой консультации): consultation-service отдаёт таким токеном 403 «доступ запрещён»,
     * хотя по коду комнаты гостевой вход разрешён. В теле запроса флаг не отправляем.
     */
    generateVideoRoomToken: build.mutation<IVideoRoomTokenResponse, IVideoRoomTokenRequest & { as_guest?: boolean }>({
      query: ({ as_guest, ...data }) => ({
        url: '/consultations/generate-token',
        method: 'POST',
        body: JSON.stringify(data),
        headers: as_guest ? { [GUEST_REQUEST_HEADER]: '1' } : undefined,
      }),
    }),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getConsultations: build.query<any[], IConsultationsParams>({
      providesTags: () => ['Consultations'],
      query: (params) => ({
        url: '/consultations',
        params: {
          ...params,
        },
      }),
    }),
    getAllConsultations: build.query<IConsultation[], void>({
      providesTags: () => ['Consultations'],
      query: () => ({
        url: '/consultations',
      }),
    }),
    createConsultation: build.mutation<IConsultation, Partial<IConsultation>>({
      query: (data) => ({
        url: '/consultations',
        method: 'POST',
        body: JSON.stringify(data),
      }),
      invalidatesTags: (result, error) => (error ? [] : ['Consultations']),
    }),
    checkOfflineAudioAvailability: build.mutation<{ available: true }, Partial<IConsultation>>({
      query: (data) => ({
        url: '/consultations/availability',
        method: 'POST',
        body: JSON.stringify(data),
      }),
    }),
    updateOfflineAudioLifecycle: build.mutation<
      IConsultation,
      { id: number; lifecycle_status: 'pending_payment' | 'processing' | 'ready' | 'cancelled' }
    >({
      query: ({ id, lifecycle_status }) => ({
        url: `/consultations/${id}/offline-audio-lifecycle`,
        method: 'POST',
        body: JSON.stringify({ lifecycle_status }),
      }),
      invalidatesTags: ['Consultations'],
    }),
    changeConsultation: build.mutation<IConsultation, Partial<IConsultation>>({
      query: (data) => ({
        url: `/consultations/${data.id}`,
        method: 'PUT',
        body: JSON.stringify(data),
      }),
      invalidatesTags: (result, error) => (error ? [] : ['Consultations']),
    }),
  }),
});

export const {
  useLazyGetConsultationQuery,
  useLazyGetConsultationByCodeQuery,
  useLazyGetConsultationsQuery,
  useLazyGetAllConsultationsQuery,
  useCreateConsultationMutation,
  useCheckOfflineAudioAvailabilityMutation,
  useUpdateOfflineAudioLifecycleMutation,
  useChangeConsultationMutation,
  useGetConsultationQuery,
  useEditConsultationMutation,
  useGetConsultationsQuery,
  useDeleteConsultationMutation,
  useUpdateConsultationMutation,
  useGenerateVideoRoomTokenMutation,
} = consultationsApi;
